import type { MediaUploadError } from './../../ricos-content';
import type { EventSource, IMediaPluginService, INotifier, PublisherProvider } from './../../ricos-types';
import type { UpdateService } from './../upload/update-service';
import type { IFileUploader } from './../../plugin-commons/Utils/Uploader';

import {
  createUploadEndBIData,
  createUploadStartBIData,
} from '../../ricos-common/biCallbacks/mediaUploadBI';

export type UploadTopics = [
  'ricos.upload.functionality.uploadStarted',
  'ricos.upload.functionality.uploadFinished'
];

export type IUploadService = UploadService;
export class UploadService implements EventSource<UploadTopics> {
  getErrorNotifier!: () => INotifier;

  updateService: UpdateService;

  getHiddenInputElement!: () => HTMLInputElement;

  onInputChange: ((this: HTMLInputElement, event: any) => any) | null;

  constructor(updateService: UpdateService) {
    this.updateService = updateService;
    this.onInputChange = null;
  }

  topicsToPublish: UploadTopics = [
    'ricos.upload.functionality.uploadStarted',
    'ricos.upload.functionality.uploadFinished',
  ];

  publishers!: PublisherProvider<UploadTopics>;

  setErrorNotifier(getErrorNotifer: () => INotifier): void {
    this.getErrorNotifier = getErrorNotifer;
  }

  setHiddenInputElement(getHiddenInputElement: () => HTMLInputElement): void {
    this.getHiddenInputElement = getHiddenInputElement;
  }

  notifyError(error: MediaUploadError): void {
    this.getErrorNotifier().notify(error);
  }

  /** @deprecated use selectFiles */
  selectFilesCallback(
    accept = 'image/*',
    multiple: boolean,
    callback: (files: File[]) => void
  ): void {
    const hiddenInputElement = this.getHiddenInputElement();
    this.onInputChange && hiddenInputElement.removeEventListener('change', this.onInputChange);
    this.onInputChange = event => {
      const files: File[] = Array.from(event.target.files || []);
      // if we pass null userEvents in integration tests will fail, so we pass alternative value of ''
      event.target.value = '';
      callback(files);
    };
    hiddenInputElement.addEventListener('change', this.onInputChange, { once: true });
    hiddenInputElement.accept = accept;
    hiddenInputElement.multiple = !!multiple;
    hiddenInputElement.click();
  }

  selectFiles({
    accept = 'image/*',
    multiple = true,
  }: {
    accept?: string;
    multiple?: boolean;
  }): Promise<File[]> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return new Promise<File[]>(resolve => {
      const hiddenInputElement = that.getHiddenInputElement();
      that.onInputChange && hiddenInputElement.removeEventListener('change', that.onInputChange);
      that.onInputChange = event => {
        const files: File[] = Array.from(event.target.files || []);
        // if we pass null userEvents in integration tests will fail, so we pass alternative value of ''
        event.target.value = '';
        resolve(files);
      };
      hiddenInputElement.addEventListener('change', that.onInputChange, { once: true });
      hiddenInputElement.accept = accept;
      hiddenInputElement.multiple = !!multiple;
      hiddenInputElement.click();
    });
  }

  trackUploadBi = (
    type: string,
    fileSize: number,
    fileType: string
  ): { start: () => void; success: () => void; error: (e: any) => void } => {
    const uploadStartBiData = createUploadStartBIData(type, fileSize, fileType);
    return {
      start: () =>
        this.publishers
          .byTopic('ricos.upload.functionality.uploadStarted')
          .publish(uploadStartBiData),
      success: () => {
        this.publishers
          .byTopic('ricos.upload.functionality.uploadFinished')
          .publish(createUploadEndBIData(uploadStartBiData));
      },
      error: (error: any) => {
        this.publishers
          .byTopic('ricos.upload.functionality.uploadFinished')
          .publish(createUploadEndBIData(uploadStartBiData, error));
      },
    };
  };

  async uploadFile(
    file: File,
    nodeId: string,
    uploader: IFileUploader,
    type: string,
    mediaPluginService: IMediaPluginService,
    fileState?: Record<string, string | number>
  ): Promise<void> {
    try {
      const url = URL.createObjectURL(file);

      // If we set upload state synchronously the node won't be in state
      // yet and its update will fail
      await Promise.resolve();

      const newFileState = this.updateService.updateLoadingState(
        url,
        file,
        nodeId,
        type,
        mediaPluginService,
        fileState
      );
      const uploadStartBiData = createUploadStartBIData(type, file.size, file.type);
      this.publishers
        .byTopic('ricos.upload.functionality.uploadStarted')
        .publish(uploadStartBiData);

      let error: MediaUploadError | undefined;
      try {
        const uploadedFile = await uploader.upload(file);
        this.updateService.updatePluginData(
          uploadedFile,
          nodeId,
          type,
          mediaPluginService,
          newFileState
        );
        URL.revokeObjectURL(url as string);
      } catch (e: any) {
        error = e;
        this.notifyError(e);
        this.updateService.updateErrorState(e, nodeId, type, mediaPluginService, newFileState);
      } finally {
        const uploadFinishedBiData = createUploadEndBIData(uploadStartBiData, error);
        this.publishers
          .byTopic('ricos.upload.functionality.uploadFinished')
          .publish(uploadFinishedBiData);
      }
    } catch (e: any) {
      this.notifyError(e);
    }
  }
}
